package co.maytes.standupbot.dashboard.features.settings

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.styles.FocusRing
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.SettingsClasses
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onKeyDown
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.tabIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.attributes.colspan
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr

data class LogTableColumn(
    val label: String,
    val width: String? = null,
    val end: Boolean = false,
)

object LogTableClasses {
    const val TABLE = "sb-log-table"
    const val ROW = "sb-log-row"
    const val END = "sb-log-end"
    const val DETAIL = "sb-log-detail"
    const val FILL = "sb-log-fill"
    const val WRAP = "sb-log-wrap"
    const val CLIP = "sb-log-clip"
    const val CAP = "sb-log-cap"
    const val LINE_HEIGHT = "1.25rem"
}

enum class LogCellFlow { Fit, Fill, Wrap }

private val LOADING_BAR_WIDTHS = listOf("70%", "55%", "85%", "45%")
private const val KEY_ENTER = "Enter"
private const val KEY_SPACE = " "

@Composable
fun LogTable(
    columns: List<LogTableColumn>,
    ariaLabel: String,
    body: @Composable () -> Unit,
) {
    Div(attrs = Modifier.fillMaxWidth().styleModifier { property("overflow-x", "auto") }.toAttrs()) {
        Table(
            attrs = Modifier
                .classNames(LogTableClasses.TABLE)
                .attrsModifier { attr("aria-label", ariaLabel) }
                .toAttrs(),
        ) {
            Thead {
                Tr {
                    columns.forEach { column ->
                        Th(
                            attrs = Modifier
                                .let { if (column.end) it.classNames(LogTableClasses.END) else it }
                                .styleModifier { column.width?.let { property("width", it) } }
                                .attrsModifier { attr("scope", "col") }
                                .toAttrs(),
                        ) { Text(column.label) }
                    }
                }
            }
            Tbody { body() }
        }
    }
}

@Composable
fun LogTableRow(
    columnCount: Int,
    expanded: Boolean,
    onToggle: () -> Unit,
    detail: @Composable () -> Unit,
    cells: @Composable () -> Unit,
) {
    Tr(
        attrs = Modifier
            .classNames(LogTableClasses.ROW, FocusRing.VISIBLE_CLASS)
            .tabIndex(0)
            .onClick { onToggle() }
            .onKeyDown { event ->
                if (event.key == KEY_ENTER || event.key == KEY_SPACE) {
                    event.preventDefault()
                    onToggle()
                }
            }
            .attrsModifier { attr("aria-expanded", expanded.toString()) }
            .toAttrs(),
    ) { cells() }
    if (expanded) {
        Tr {
            Td(attrs = Modifier.classNames(LogTableClasses.DETAIL).toAttrs { colspan(columnCount) }) { detail() }
        }
    }
}

@Composable
fun LogCell(
    end: Boolean = false,
    flow: LogCellFlow = LogCellFlow.Fit,
    content: @Composable () -> Unit,
) {
    Td(
        attrs = Modifier
            .let { if (end) it.classNames(LogTableClasses.END) else it }
            .let {
                when (flow) {
                    LogCellFlow.Fit -> it
                    LogCellFlow.Fill -> it.classNames(LogTableClasses.FILL)
                    LogCellFlow.Wrap -> it.classNames(LogTableClasses.WRAP)
                }
            }
            .toAttrs(),
    ) { content() }
}

@Composable
fun LogCellText(
    text: String,
    modifier: Modifier = Modifier,
    color: CSSColorValue = Palette.textPrimary,
    mono: Boolean = false,
    clip: Boolean = false,
    capped: Boolean = false,
    title: String? = null,
) {
    SpanText(
        text = text,
        modifier = Modifier
            .color(color)
            .fontSize(if (mono) Typography.caption.size else Typography.bodySmall.size)
            .fontWeight(Typography.bodySmall.weight)
            .let { if (mono) it.fontFamily(*Typography.MONO_STACK) else it }
            .let { if (clip) it.classNames(LogTableClasses.CLIP) else it }
            .let { if (capped) it.classNames(LogTableClasses.CAP) else it }
            .let { if (title != null) it.attrsModifier { attr("title", title) } else it }
            .then(modifier),
    )
}

@Composable
fun LogCellMissing() {
    LogCellText(text = "–", color = Palette.textTertiary)
}

@Composable
fun LogTableLoadingRows(columns: List<LogTableColumn>, rows: Int) {
    repeat(rows) { rowIndex ->
        Tr {
            columns.forEachIndexed { columnIndex, column ->
                Td(attrs = Modifier.let { if (column.end) it.classNames(LogTableClasses.END) else it }.toAttrs()) {
                    Div(
                        attrs = Modifier
                            .styleModifier {
                                property("display", "flex")
                                property("align-items", "center")
                                property("height", LogTableClasses.LINE_HEIGHT)
                                property("justify-content", if (column.end) "flex-end" else "flex-start")
                            }
                            .toAttrs(),
                    ) {
                        Box(
                            modifier = Modifier
                                .classNames(SettingsClasses.BAR)
                                .styleModifier {
                                    property("height", Spacing.md.toString())
                                    property("width", LOADING_BAR_WIDTHS[(rowIndex + columnIndex) % LOADING_BAR_WIDTHS.size])
                                },
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun LogDetailList(rows: List<LogDetailRow>) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(Palette.panel)
            .borderRadius(Radius.lg)
            .padding(Spacing.sm)
            .styleModifier { property("gap", Spacing.xxs.toString()) },
    ) {
        rows.forEach { row ->
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier { property("gap", Spacing.md.toString()) },
            ) {
                SpanText(
                    text = row.key,
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .fontWeight(Typography.bodySmall.weight)
                        .color(Palette.textTertiary)
                        .styleModifier { property("flex", "0 0 6rem") },
                )
                SpanText(
                    text = row.value,
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .fontWeight(Typography.bodySmall.weight)
                        .color(Palette.textSecondary)
                        .let { if (row.mono) it.fontFamily(*Typography.MONO_STACK) else it }
                        .styleModifier {
                            property("min-width", "0")
                            property("white-space", "pre-wrap")
                            property("overflow-wrap", "anywhere")
                        },
                )
            }
        }
    }
}
